import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  IoArrowUpSharp,
  IoArrowDownSharp,
  IoChevronForward,
} from "react-icons/io5";
import morningIcon from "../img/morning.png";
import afternoonIcon from "../img/cloud.png";
import eveningIcon from "../img/night.png";

function Info({
  selectedYear,
  setSelectedYear,
  selectedQuarter,
  setSelectedQuarter,
}) {
  const [greeting, setGreeting] = useState("");
  const [imgIcon, setImgIcon] = useState(morningIcon);
  const [userName, setUserName] = useState("");
  const [institutionMetric, setInstitutionMetric] = useState(null);
  const [passingMetric, setPassingMetric] = useState(null);
  const [attendanceMetric, setAttendanceMetric] = useState(null);
  const [newEnrollmentMetric, setNewEnrollmentMetric] = useState(null);
  const [progressionMetric, setProgressionMetric] = useState(null);
  const [attritionVarianceMetric, setAttritionVarianceMetric] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const getCurrentQuarterNumber = () => {
    if (currentMonth < 3) return 1;
    if (currentMonth < 6) return 2;
    if (currentMonth < 9) return 3;
    return 4;
  };

  const currentQuarterNumber = getCurrentQuarterNumber();
  const quarters = [1, 2, 3, 4];
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (!selectedYear || !selectedQuarter) return;

    if (
      selectedYear === currentYear &&
      selectedQuarter > currentQuarterNumber
    ) {
      setSelectedYear(currentYear - 1);
    }
  }, [
    selectedQuarter,
    selectedYear,
    currentQuarterNumber,
    currentYear,
    setSelectedYear,
  ]);

  useEffect(() => {
    if (!selectedQuarter || !selectedYear) {
      let prevQuarter = currentQuarterNumber - 1;
      let prevYear = currentYear;

      if (currentQuarterNumber === 1) {
        prevQuarter = 4;
        prevYear = currentYear - 1;
      }

      if (!selectedQuarter) setSelectedQuarter(prevQuarter);
      if (!selectedYear) setSelectedYear(prevYear);
    }
  }, [
    selectedQuarter,
    selectedYear,
    currentQuarterNumber,
    currentYear,
    setSelectedQuarter,
    setSelectedYear,
  ]);

  useEffect(() => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      setGreeting("Good Morning");
      setImgIcon(morningIcon);
    } else if (hour >= 12 && hour < 17) {
      setGreeting("Good Afternoon");
      setImgIcon(afternoonIcon);
    } else {
      setGreeting("Good Evening");
      setImgIcon(eveningIcon);
    }

    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.user_name) {
      setUserName(user.user_name);
    }
  }, []);

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleQuarterChange = (e) => {
    const q = parseInt(e.target.value);

    if (selectedYear === currentYear && q > currentQuarterNumber) {
      setSelectedYear(currentYear - 1);
    }

    setSelectedQuarter(q);
  };

  const fetchInstitutionMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getClassSizeByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
      );

      if (!res.data.success || !res.data.current || !res.data.previous) {
        console.warn("Invalid or failed response:", res.data);
        return;
      }

      const { current, previous } = res.data;

      const currTotal = current.reduce(
        (sum, item) => sum + parseInt(item.course_count),
        0
      );
      const prevTotal = previous.reduce(
        (sum, item) => sum + parseInt(item.course_count),
        0
      );
      const diff = currTotal - prevTotal;

      setInstitutionMetric({
        title: "Class Size Variance",
        value: Math.abs(diff),
        diff: `${currTotal} - ${prevTotal}`,
        up: diff >= 0,
      });

      // console.log("Total:", currTotal, "-", prevTotal);
    } catch (err) {
      console.error("Failed to fetch Institution Total:", err);
    }
  }, [selectedQuarter, selectedYear]);

  const fetchPassingRateMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getPassingRateByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
      );

      if (!res.data.success || !res.data.current || !res.data.previous) {
        console.warn("Invalid Passing Rate response:", res.data);
        return;
      }

      const { current, previous } = res.data;

      const avg = (arr) =>
        arr.length > 0
          ? (
              arr.reduce(
                (sum, item) => sum + parseFloat(item.totalRate || 0),
                0
              ) / arr.length
            ).toFixed(2)
          : "0.00";

      const currentAvg = avg(current);
      const previousAvg = avg(previous);
      const diff = (parseFloat(currentAvg) - parseFloat(previousAvg)).toFixed(
        2
      );
      const isUp = parseFloat(diff) >= 0;

      const metric = {
        title: "Passing Rate Variance",
        value: `${Math.abs(diff)}%`,
        diff: `${currentAvg}% - ${previousAvg}%`,
        up: isUp,
      };

      setPassingMetric(metric);
    } catch (err) {
      console.error("Failed to fetch Passing Rate metric:", err);
    }
  }, [selectedQuarter, selectedYear]);

  const fetchAttendanceMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getAttendanceByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
      );

      if (!res.data.success || !res.data.current || !res.data.previous) {
        console.warn("Invalid Attendance response:", res.data);
        return;
      }

      const { current, previous } = res.data;

      const avg = (arr) =>
        arr.length > 0
          ? (
              arr.reduce(
                (sum, item) => sum + parseFloat(item.course_att_rate || 0),
                0
              ) / arr.length
            ).toFixed(2)
          : "0.00";

      const currentAvg = avg(current);
      const previousAvg = avg(previous);
      const diff = (parseFloat(currentAvg) - parseFloat(previousAvg)).toFixed(
        2
      );
      const isUp = parseFloat(diff) >= 0;

      const metric = {
        title: "Attendance Variance",
        value: `${Math.abs(diff)}%`,
        diff: `${currentAvg}% - ${previousAvg}%`,
        up: isUp,
      };

      setAttendanceMetric(metric);
    } catch (err) {
      console.error("Failed to fetch Attendance metric:", err);
    }
  }, [selectedQuarter, selectedYear]);

  const fetchNewEnrollmentMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getNewEnrollmentByLevel2?qtr=${selectedQuarter}&year=${selectedYear}`
      );
      if (!res.data.success || !res.data.current || !res.data.previous) return;
      const { current, previous } = res.data;
      const currTotal = current.reduce(
        (sum, item) => sum + parseInt(item.totalLevel || 0),
        0
      );
      const prevTotal = previous.reduce(
        (sum, item) => sum + parseInt(item.totalLevel || 0),
        0
      );
      const diff = currTotal - prevTotal;
      setNewEnrollmentMetric({
        title: "New Enrollment Variance",
        value: Math.abs(diff),
        diff: `${currTotal} - ${prevTotal}`,
        up: diff >= 0,
      });
    } catch (err) {
      console.error("Failed to fetch New Enrollment metric:", err);
    }
  }, [selectedQuarter, selectedYear]);

  const fetchProgressionMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getProgressionRate?qtr=${selectedQuarter}&year=${selectedYear}`
      );

      if (!res.data.success || !res.data.current || !res.data.previous) {
        console.warn("Invalid progression response:", res.data);
        return;
      }

      const currentData = res.data.current.summary || [];
      const previousData = res.data.previous.summary || [];

      const currEnrolled = currentData.reduce(
        (sum, item) => sum + parseInt(item.enrolled_count || 0),
        0
      );
      const currEligible = currentData.reduce(
        (sum, item) => sum + parseInt(item.eligible_count || 0),
        0
      );

      const prevEnrolled = previousData.reduce(
        (sum, item) => sum + parseInt(item.enrolled_count || 0),
        0
      );
      const prevEligible = previousData.reduce(
        (sum, item) => sum + parseInt(item.eligible_count || 0),
        0
      );

      const currRate =
        currEligible > 0
          ? ((currEnrolled / currEligible) * 100).toFixed(2)
          : "0.00";
      const prevRate =
        prevEligible > 0
          ? ((prevEnrolled / prevEligible) * 100).toFixed(2)
          : "0.00";

      const diff = (parseFloat(currRate) - parseFloat(prevRate)).toFixed(2);
      const isUp = parseFloat(diff) >= 0;

      const metric = {
        title: "Progression Rate Variance",
        value: `${Math.abs(diff)}%`,
        diff: `${currRate}% - ${prevRate}%`,
        up: isUp,
      };

      setProgressionMetric(metric);
    } catch (err) {
      console.error("Failed to fetch progression metric:", err);
    }
  }, [selectedQuarter, selectedYear]);

  const fetchAttritionVarianceMetric = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/getAttritionRate?qtr=${selectedQuarter}&year=${selectedYear}`
      );

      if (!res.data.success || !res.data.current || !res.data.previous) {
        console.warn("Invalid attrition response:", res.data);
        return;
      }

      const currentData = res.data.current;
      const previousData = res.data.previous;

      const currWithdrawn = currentData.reduce(
        (sum, item) => sum + parseInt(item.totalWithdrawn || 0),
        0
      );

      const prevWithdrawn = previousData.reduce(
        (sum, item) => sum + parseInt(item.totalWithdrawn || 0),
        0
      );

      const diff = currWithdrawn - prevWithdrawn;
      const isUp = diff >= 0;

      const metric = {
        title: "Attrition Variance",
        value: `${Math.abs(diff)}`,
        diff: `${currWithdrawn} - ${prevWithdrawn}`,
        up: isUp,
      };

      setAttritionVarianceMetric(metric);
    } catch (err) {
      console.error("Failed to fetch attrition variance metric:", err);
    }
  }, [selectedQuarter, selectedYear]);

  useEffect(() => {
    if (selectedQuarter && selectedYear) {
      fetchInstitutionMetric();
      fetchPassingRateMetric();
      fetchAttendanceMetric();
      fetchNewEnrollmentMetric();
      fetchProgressionMetric();
      fetchAttritionVarianceMetric();
    }
  }, [
    selectedQuarter,
    selectedYear,
    fetchInstitutionMetric,
    fetchPassingRateMetric,
    fetchAttendanceMetric,
    fetchNewEnrollmentMetric,
    fetchProgressionMetric,
    fetchAttritionVarianceMetric,
  ]);

  let currentLabel = null;
  let previousLabel = null;

  if (selectedQuarter && selectedYear) {
    const prevQuarter = selectedQuarter === 1 ? 4 : selectedQuarter - 1;
    const prevYear = selectedQuarter === 1 ? selectedYear - 1 : selectedYear;

    currentLabel = `Q${selectedQuarter} ${selectedYear}`;
    previousLabel = `Q${prevQuarter} ${prevYear}`;
  }

  const allMetrics = [
    ...(institutionMetric ? [institutionMetric] : []),
    ...(attendanceMetric ? [attendanceMetric] : []),
    ...(passingMetric ? [passingMetric] : []),
    ...(progressionMetric ? [progressionMetric] : []),
    ...(newEnrollmentMetric ? [newEnrollmentMetric] : []),
    ...(attritionVarianceMetric ? [attritionVarianceMetric] : []),
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        setCurrentIndex((prev) =>
          prev + 1 > allMetrics.length - 4 ? 0 : prev + 1
        );
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [allMetrics.length]);

  const selectStyle = {
    padding: "6px 10px",
    borderRadius: 4,
    backgroundColor: "#4A4A4A",
    color: "#fff",
    border: "1px solid #555",
    outline: "none",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        padding: "20px",
        gap: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div>
        <h3
          style={{
            fontWeight: "bold",
            fontSize: 28,
            fontFamily: "LibreFranklin-ExtraBold",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {greeting}
          <img
            src={imgIcon}
            alt="weather icon"
            style={{ width: 40, height: 40 }}
          />
        </h3>
        <p style={{ fontSize: 16 }}>{userName}</p>

        <div style={{ display: "flex", alignItems: "center", gap: 50 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={selectedQuarter || ""}
              onChange={handleQuarterChange}
              style={selectStyle}
            >
              <option disabled value="">
                Select Quarter
              </option>
              {quarters.map((q) => {
                const isDisabled =
                  selectedYear === currentYear && q > currentQuarterNumber;
                return (
                  <option key={q} value={q} disabled={isDisabled}>
                    Q{q}
                  </option>
                );
              })}
            </select>

            <select
              value={selectedYear}
              onChange={handleYearChange}
              style={selectStyle}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {currentLabel && previousLabel && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  backgroundColor: "#2C2C2C",
                  padding: "6px 12px",
                  borderRadius: 6,
                  color: "#fff",
                  boxShadow:
                    "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
                }}
              >
                {currentLabel}
              </span>
              <span style={{ fontSize: 18, color: "#fff" }}>vs</span>
              <span
                style={{
                  backgroundColor: "#2C2C2C",
                  padding: "6px 12px",
                  borderRadius: 6,
                  color: "#fff",
                  boxShadow:
                    "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
                }}
              >
                {previousLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
        <div style={{ display: "flex", gap: 15 }}>
          {allMetrics.slice(currentIndex, currentIndex + 4).map((m, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: "#2C2C2C",
                padding: "15px 20px",
                borderRadius: 10,
                minWidth: 180,
                boxShadow:
                  "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
                color: "#fff",
                height: 120,
              }}
            >
              <div style={{ fontSize: 13, marginBottom: 6 }}>{m.title}</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: 25,
                  fontWeight: "bold",
                  color: m.up ? "#4CAF50" : "#F44336",
                  marginBottom: 6,
                }}
              >
                {m.value} {m.up ? <IoArrowUpSharp /> : <IoArrowDownSharp />}
              </div>
              <div style={{ fontSize: 14, color: "#ccc" }}>{m.diff}</div>
            </div>
          ))}
        </div>

        <button
          onClick={() =>
            setCurrentIndex((prev) =>
              prev + 1 > allMetrics.length - 4 ? 0 : prev + 1
            )
          }
          style={{
            backgroundColor: "#4A4A4A",
            border: "none",
            borderRadius: "50%",
            padding: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 40,
            width: 40,
          }}
        >
          <IoChevronForward color="#fff" size={24} />
        </button>
      </div>
    </div>
  );
}

export default Info;
